import type { RecordPoint } from "./types";

/** A contiguous lap, derived from the current set of boundary record indices. */
export interface LapSegment {
  /** 0-based lap number. */
  index: number;
  startIdx: number;
  endIdx: number;
  startTime: number;
  endTime: number;
  durationSec: number;
  distanceM: number | null;
  avgSpeed: number | null;
  avgPower: number | null;
  avgHeartRate: number | null;
  ascentM: number | null;
  descentM: number | null;
}

/** Cumulative distance at a record, falling back to the nearest defined neighbour. */
function distanceAt(records: RecordPoint[], idx: number): number | null {
  if (records[idx].distance !== null) return records[idx].distance;
  for (let i = idx - 1; i >= 0; i--) {
    if (records[i].distance !== null) return records[i].distance;
  }
  for (let i = idx + 1; i < records.length; i++) {
    if (records[i].distance !== null) return records[i].distance;
  }
  return null;
}

/**
 * Turn a sorted list of internal boundary indices into contiguous lap segments
 * covering the whole ride. `splits` are record indices strictly inside (0, last).
 */
export function boundariesToSegments(
  records: RecordPoint[],
  splits: number[],
): LapSegment[] {
  const last = records.length - 1;
  const clean = [...new Set(splits)]
    .filter((s) => s > 0 && s < last)
    .sort((a, b) => a - b);
  const bounds = [0, ...clean, last];

  const segments: LapSegment[] = [];
  for (let k = 0; k < bounds.length - 1; k++) {
    const startIdx = bounds[k];
    const endIdx = bounds[k + 1];
    const startTime = records[startIdx].time;
    const endTime = records[endIdx].time;

    const startDist = distanceAt(records, startIdx);
    const endDist = distanceAt(records, endIdx);
    const distanceM =
      startDist !== null && endDist !== null
        ? Math.max(0, endDist - startDist)
        : null;

    // Aggregate instantaneous fields over records in (startIdx, endIdx].
    let powerSum = 0;
    let powerN = 0;
    let hrSum = 0;
    let hrN = 0;
    let ascent = 0;
    let descent = 0;
    let ascentSeen = false;
    for (let i = startIdx + 1; i <= endIdx; i++) {
      const rec = records[i];
      if (rec.power !== null) {
        powerSum += rec.power;
        powerN++;
      }
      if (rec.heartRate !== null) {
        hrSum += rec.heartRate;
        hrN++;
      }
      const prev = records[i - 1];
      if (rec.altitude !== null && prev.altitude !== null) {
        // Cap the gap so a long auto-pause contributes no elevation change.
        const dt = (rec.time - prev.time) / 1000;
        if (dt > 0 && dt <= 3) {
          const dAlt = rec.altitude - prev.altitude;
          if (dAlt > 0) ascent += dAlt;
          else descent += -dAlt;
          ascentSeen = true;
        }
      }
    }

    const durationSec = (endTime - startTime) / 1000;
    segments.push({
      index: k,
      startIdx,
      endIdx,
      startTime,
      endTime,
      durationSec,
      distanceM,
      avgSpeed:
        distanceM !== null && durationSec > 0 ? distanceM / durationSec : null,
      avgPower: powerN > 0 ? powerSum / powerN : null,
      avgHeartRate: hrN > 0 ? hrSum / hrN : null,
      ascentM: ascentSeen ? ascent : null,
      descentM: ascentSeen ? descent : null,
    });
  }
  return segments;
}
